import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Paper,
  Card, CardContent, Chip, Grid, LinearProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableRow, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { evaluateStocks, getFinancialReport, getStockHistoricalData } from '../utils/api';
import { isLoggedIn } from '../utils/auth';
import PriceChart from '../components/PriceChart';
import GuestWall from '../components/GuestWall';
import { useI18n } from '../utils/i18n';

const GUEST_VISIBLE_DIMENSIONS = 4;

const signalConfig = {
  strong_buy: { color: 'success', icon: <TrendingUpIcon fontSize="small" />, label: 'STRONG BUY' },
  buy: { color: 'success', icon: <TrendingUpIcon fontSize="small" />, label: 'BUY' },
  hold: { color: 'warning', icon: <TrendingFlatIcon fontSize="small" />, label: 'HOLD' },
  sell: { color: 'error', icon: <TrendingDownIcon fontSize="small" />, label: 'SELL' },
  strong_sell: { color: 'error', icon: <TrendingDownIcon fontSize="small" />, label: 'STRONG SELL' },
};

const QUICK_PICKS = ['AAPL', 'NVDA', 'MSFT', 'TSLA', 'AMZN', 'GOOGL', 'SH600519', 'SZ000858', 'SH601318'];

export default function StockDetailPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTicker = searchParams.get('ticker') || '';
  const [input, setInput] = useState(initialTicker);
  const [ticker, setTicker] = useState('');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState({});
  const [error, setError] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const { t } = useI18n();

  const loadAll = useCallback(async (t_val) => {
    if (!t_val) return;
    const upper = t_val.trim().toUpperCase();
    setTicker(upper);
    setSearchParams({ ticker: upper });
    setError('');
    setEvaluation(null);
    setFinancialReport(null);
    setPriceData(null);

    setLoading((p) => ({ ...p, eval: true }));
    try {
      const data = await evaluateStocks([upper]);
      setEvaluation(data[upper] || null);
    } catch (e) { setError(e.message); }
    finally { setLoading((p) => ({ ...p, eval: false })); }

    setLoading((p) => ({ ...p, finance: true, price: true }));
    getFinancialReport(upper, 3)
      .then((d) => setFinancialReport(d.data || d))
      .catch(() => {})
      .finally(() => setLoading((p) => ({ ...p, finance: false })));
    getStockHistoricalData(upper, 180)
      .then((d) => setPriceData(d.data || d))
      .catch(() => {})
      .finally(() => setLoading((p) => ({ ...p, price: false })));
  }, [setSearchParams]);

  useEffect(() => {
    if (initialTicker && !ticker) loadAll(initialTicker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const val = input.trim();
    if (!val) return;
    const valid = /^[A-Za-z]{1,5}(\.[A-Za-z])?$|^(SH|SZ|sh|sz)?[0-9]{6}$/;
    if (!valid.test(val)) { setError(t('stock.invalidTicker')); return; }
    loadAll(val);
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  const signal = (evaluation?.signal || 'hold').toLowerCase();
  const config = signalConfig[signal] || signalConfig.hold;
  const isCn = evaluation?.market === 'CN' || !!evaluation?.stock_name;
  const currency = isCn ? '¥' : '$';

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" sx={{ mb: 0.5 }}>{t('stock.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {t('stock.subtitle')}
      </Typography>

      {/* Search toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField placeholder={t('stock.placeholder')} size="small"
            value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 } }} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}
            disabled={loading.eval || !input.trim()}>
            {loading.eval ? t('stock.analyzing') : t('stock.analyze')}
          </Button>
        </Box>
        <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">{t('stock.quick')}</Typography>
          {QUICK_PICKS.map((s) => (
            <Chip key={s} label={s} size="small" variant="outlined"
              onClick={() => { setInput(s); loadAll(s); }} sx={{ cursor: 'pointer', fontSize: 12 }} />
          ))}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading.eval && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}

      {evaluation && !loading.eval && (
        <>
          {/* Hero header card */}
          <Card sx={{ mb: 2.5 }}>
            <CardContent sx={{ py: 2.5 }}>
              <Grid container spacing={2} alignItems="center">
                {/* Left: ticker + signal + price */}
                <Grid size={{ xs: 12, sm: 7 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>{ticker}</Typography>
                    {evaluation.stock_name && (
                      <Typography variant="body1" color="text.secondary">{evaluation.stock_name}</Typography>
                    )}
                    <Chip icon={config.icon} label={config.label} color={config.color} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="h3" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
                      {currency}{(evaluation.current_price || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body1"
                      color={(evaluation.upside_potential || 0) >= 0 ? 'success.main' : 'error.main'}>
                      {t('stock.target')}: {currency}{(evaluation.target_price || 0).toFixed(2)}
                      {' '}({(evaluation.upside_potential || 0) >= 0 ? '+' : ''}{(evaluation.upside_potential || 0).toFixed(1)}%)
                    </Typography>
                  </Box>
                </Grid>
                {/* Right: score gauge */}
                <Grid size={{ xs: 12, sm: 5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">{t('stock.overallScore')}</Typography>
                      <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                        {(evaluation.ranking_score || 0).toFixed(0)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">/ 100</Typography>
                    </Box>
                    <Box sx={{ flex: 1, maxWidth: 160 }}>
                      <LinearProgress variant="determinate" value={Math.min(evaluation.ranking_score || 0, 100)}
                        color={config.color} sx={{ height: 10, borderRadius: 5 }} />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Paper sx={{ mb: 2.5 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              <Tab label={t('stock.tabScores')} />
              <Tab label={t('stock.tabChart')} />
              <Tab label={t('stock.tabFinancials')} />
            </Tabs>
          </Paper>

          {tab === 0 && <ScoreBreakdownPanel evaluation={evaluation} currency={currency} isGuest={!isLoggedIn()} />}
          {tab === 1 && (
            !isLoggedIn() ? (
              <GuestWall visible height={300}>
                <Box sx={{ height: 300, background: 'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, transparent 100%)', borderRadius: 1 }} />
              </GuestWall>
            ) : loading.price
              ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
              : priceData ? <PriceChart data={priceData} ticker={ticker} />
              : <Typography color="text.secondary">{t('stock.noPriceData')}</Typography>
          )}
          {tab === 2 && (
            !isLoggedIn() ? (
              <GuestWall visible height={300}>
                <Box sx={{ height: 300, background: 'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, transparent 100%)', borderRadius: 1 }} />
              </GuestWall>
            ) : loading.finance
              ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
              : financialReport ? <FinancialsPanel data={financialReport} />
              : <Typography color="text.secondary">{t('stock.noFinancialData')}</Typography>
          )}

        </>
      )}
    </Box>
  );
}


/* ── Score Breakdown Panel ── */
function ScoreBreakdownPanel({ evaluation, currency, isGuest }) {
  const e = evaluation;
  const isCn = e.market === 'CN' || !!e.stock_name;
  const { t, lang } = useI18n();

  const dimensions = [
    { label: t('stock.fiveDayTrend'), score: e.five_day_score, value: `${(e.five_day_trend || 0).toFixed(1)}%` },
    { label: t('stock.oneMonthTrend'), score: e.one_month_score, value: `${(e.one_month_trend || 0).toFixed(1)}%` },
    { label: t('stock.threeMonthTrend'), score: e.trend_score, value: `${(e.three_month_trend || 0).toFixed(1)}%` },
    { label: t('stock.volAdjReturn'), score: e.vol_adj_score, value: e.volatility_adjusted_return != null ? `${Number(e.volatility_adjusted_return).toFixed(2)}` : 'N/A' },
    { label: t('stock.rsiSignal'), score: e.rsi_score, value: e.rsi != null ? `${Number(e.rsi).toFixed(0)}` : 'N/A' },
    { label: t('stock.peValuation'), score: e.pe_score, value: e.pe_ratio ? e.pe_ratio.toFixed(1) : 'N/A' },
    { label: t('stock.earningsGrowth'), score: e.earnings_growth_score, value: e.eps_growth != null ? `${Number(e.eps_growth).toFixed(1)}%` : 'N/A' },
    { label: t('stock.financialHealth'), score: e.financial_health_score, value: e.roe != null ? `ROE ${Number(e.roe).toFixed(1)}%` : 'N/A' },
    { label: t('stock.betaRisk'), score: e.beta_score, value: (e.beta || 0).toFixed(2) },
    { label: t('stock.marketCap'), score: e.market_cap_score, value: fmtCap(e.market_cap, isCn) },
    { label: t('stock.dividendYield'), score: e.dividend_score, value: `${(e.dividend_yield || 0).toFixed(2)}%` },
    { label: t('stock.sectorMomentum'), score: e.sector_momentum_score, value: e.sector_momentum_score != null ? `${Number(e.sector_momentum_score).toFixed(0)}/100` : 'N/A' },
  ];

  const visibleDimensions = isGuest ? dimensions.slice(0, GUEST_VISIBLE_DIMENSIONS) : dimensions;
  const hiddenDimensions = isGuest ? dimensions.slice(GUEST_VISIBLE_DIMENSIONS) : [];

  return (
    <Grid container spacing={2}>
      {/* Score bars */}
      <Grid size={{ xs: 12, md: 7 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>{t('stock.scoreDimensions')}</Typography>
            {visibleDimensions.map((d) => (
              <Box key={d.label} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="caption" color="text.secondary">{d.label}</Typography>
                  <Typography variant="caption">{d.value} — {(d.score || 0).toFixed(0)}/100</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min(d.score || 0, 100)}
                  color={d.score >= 65 ? 'success' : d.score >= 40 ? 'warning' : 'error'}
                  sx={{ height: 6, borderRadius: 3 }} />
              </Box>
            ))}
            {hiddenDimensions.length > 0 && (
              <GuestWall visible height={200}>
                {hiddenDimensions.map((d) => (
                  <Box key={d.label} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography variant="caption" color="text.secondary">{d.label}</Typography>
                      <Typography variant="caption">{d.value} — {(d.score || 0).toFixed(0)}/100</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(d.score || 0, 100)}
                      color="warning" sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                ))}
              </GuestWall>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Right column: metrics + strengths/risks */}
      <Grid size={{ xs: 12, md: 5 }}>
        <GuestWall visible={isGuest} height={280}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('stock.keyMetrics')}</Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <MetricRow label={t('stock.currentPrice')} value={`${currency}${(e.current_price || 0).toFixed(2)}`} />
                    <MetricRow label={t('stock.targetPrice')} value={`${currency}${(e.target_price || 0).toFixed(2)}`} />
                    <MetricRow label={t('stock.upside')} value={`${(e.upside_potential || 0).toFixed(1)}%`}
                      color={(e.upside_potential || 0) >= 0 ? 'success.main' : 'error.main'} />
                    <MetricRow label={t('stock.confidence')} value={`${(e.confidence || 0).toFixed(0)}%`} />
                    {e.sector && <MetricRow label={t('stock.sector')} value={e.sector} />}
                    {e.market_cap_category && (
                      <MetricRow label={t('stock.capCategory')} value={e.market_cap_category.replace(/_/g, ' ')} />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              {((lang === 'zh' ? e.key_strengths_zh : null) || e.key_strengths)?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" color="success.main" sx={{ mb: 0.5 }}>{t('stock.strengths')}</Typography>
                  {((lang === 'zh' ? e.key_strengths_zh : null) || e.key_strengths).map((s, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', mb: 0.3, lineHeight: 1.5 }}>✓ {s}</Typography>
                  ))}
                </Box>
              )}
              {((lang === 'zh' ? e.key_risks_zh : null) || e.key_risks)?.length > 0 && (
                <>
                  {((lang === 'zh' ? e.key_strengths_zh : null) || e.key_strengths)?.length > 0 && (
                    <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }} />
                  )}
                  <Box>
                    <Typography variant="subtitle2" color="error.main" sx={{ mb: 0.5 }}>{t('stock.risks')}</Typography>
                    {((lang === 'zh' ? e.key_risks_zh : null) || e.key_risks).map((r, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', mb: 0.3, lineHeight: 1.5 }}>⚠ {r}</Typography>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
        </GuestWall>
      </Grid>

      {/* Reasoning — full width */}
      {e.reasoning && (
        <Grid size={12}>
          <GuestWall visible={isGuest} height={120}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('stock.analysisSummary')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {(lang === 'zh' ? e.reasoning_zh : null) || e.reasoning}
              </Typography>
            </CardContent>
          </Card>
          </GuestWall>
        </Grid>
      )}
    </Grid>
  );
}

function MetricRow({ label, value, color }) {
  return (
    <TableRow>
      <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.6, pl: 0 }}>{label}</TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.6, fontWeight: 600, color: color || 'text.primary' }}>{value}</TableCell>
    </TableRow>
  );
}

function fmtCap(v, isCn) {
  if (!v) return 'N/A';
  const n = Number(v);
  if (isCn) return `${(n / 1e8).toFixed(0)}亿`;
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  return `${n.toLocaleString()}`;
}


/* ── Financials Panel ── */
function FinancialsPanel({ data }) {
  const report = data?.report || data;
  const overview = report?.company_overview || {};
  const balance = report?.balance_sheet_analysis || {};
  const valuation = report?.valuation_metrics || {};
  const pricePerf = report?.price_performance || {};
  const earnings = report?.earnings_analysis || {};
  const profitability = report?.profitability_metrics || {};
  const cashFlow = report?.cash_flow_analysis || {};
  const marketComp = report?.market_comparison || {};
  const { t } = useI18n();

  const pct = (v) => `${Number(v).toFixed(2)}%`;
  const usd = (v) => `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const bigNum = (v) => {
    const n = Number(v);
    if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
    if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    return usd(n);
  };

  const currentPrice = pricePerf.current_price || 0;
  const high52 = pricePerf['52_week_high'] || 0;
  const low52 = pricePerf['52_week_low'] || 0;
  const fromHigh = high52 > 0 ? ((currentPrice - high52) / high52 * 100) : 0;
  const currentRatio = balance.current_ratio;
  const debtRatio = balance.debt_to_assets_ratio;

  const qEps = earnings.quarterly_eps || {};
  const epsDates = Object.keys(qEps).sort().reverse();
  const latestEps = epsDates.length > 0 ? qEps[epsDates[0]] : null;
  const prevEps = epsDates.length > 1 ? qEps[epsDates[1]] : null;

  const qRev = earnings.quarterly_revenue || {};
  const revDates = Object.keys(qRev).sort().reverse();
  const latestRev = revDates.length > 0 ? qRev[revDates[0]] : null;

  const margins = profitability.profit_margins || {};
  const marginDates = Object.keys(margins).sort().reverse();
  const latestMargin = marginDates.length > 0 ? margins[marginDates[0]] : null;

  const fcf = cashFlow.free_cash_flow || {};
  const fcfDates = Object.keys(fcf).sort().reverse();
  const latestFcf = fcfDates.length > 0 ? fcf[fcfDates[0]] : null;

  const healthColor = (currentRatio >= 1.5 && debtRatio < 0.5) ? 'success.main'
    : (currentRatio >= 1.0 && debtRatio < 0.8) ? 'warning.main' : 'error.main';
  const healthLabel = (currentRatio >= 1.5 && debtRatio < 0.5) ? t('fin.strong')
    : (currentRatio >= 1.0 && debtRatio < 0.8) ? t('fin.moderate') : t('fin.weak');

  return (
    <Box>
      {/* Highlight cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">{t('fin.price')}</Typography>
            <Typography variant="h6" fontWeight={700}>{currentPrice > 0 ? usd(currentPrice) : 'N/A'}</Typography>
            <Typography variant="caption" color={fromHigh >= -10 ? 'success.main' : 'error.main'}>
              {fromHigh.toFixed(1)}% {t('fin.from52wHigh')}
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">{t('fin.ytdReturn')}</Typography>
            <Typography variant="h6" fontWeight={700}
              color={(pricePerf.ytd_return || 0) >= 0 ? 'success.main' : 'error.main'}>
              {pct(pricePerf.ytd_return || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">1Y: {pct(pricePerf.one_year_return || 0)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">{t('fin.peRatio')}</Typography>
            <Typography variant="h6" fontWeight={700}>
              {valuation.trailing_pe ? Number(valuation.trailing_pe).toFixed(1) : 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fwd: {valuation.forward_pe ? Number(valuation.forward_pe).toFixed(1) : 'N/A'}
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">{t('fin.financialHealth')}</Typography>
            <Typography variant="h6" fontWeight={700} color={healthColor}>{healthLabel}</Typography>
            <Typography variant="caption" color="text.secondary">
              CR: {currentRatio ? Number(currentRatio).toFixed(2) : 'N/A'} | D/A: {debtRatio ? Number(debtRatio).toFixed(2) : 'N/A'}
            </Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {overview.company_name || 'Company'} — {overview.sector || ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {overview.industry || ''} · Market Cap: {overview.market_cap ? bigNum(overview.market_cap) : 'N/A'}
            </Typography>
            <TableContainer><Table size="small"><TableBody>
              <FRow label={t('fin.trailingPE')} value={valuation.trailing_pe} fmt={(v) => Number(v).toFixed(1)} />
              <FRow label={t('fin.forwardPE')} value={valuation.forward_pe} fmt={(v) => Number(v).toFixed(1)} />
              <FRow label={t('fin.priceBook')} value={valuation.price_to_book} fmt={(v) => Number(v).toFixed(2)} />
              <FRow label={t('fin.pegRatio')} value={valuation.peg_ratio} fmt={(v) => Number(v).toFixed(2)} />
              <FRow label={t('fin.evEbitda')} value={valuation.ev_to_ebitda} fmt={(v) => Number(v).toFixed(1)} />
              <FRow label={t('fin.weekRange')} value={low52 > 0 ? `${usd(low52)} — ${usd(high52)}` : null} />
            </TableBody></Table></TableContainer>
          </CardContent></Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{t('fin.earningsProfitability')}</Typography>
            <TableContainer><Table size="small"><TableBody>
              <FRow label={t('fin.latestEps')} value={latestEps} fmt={(v) => usd(v)} />
              {latestEps != null && prevEps != null && prevEps !== 0 && (
                <FRow label={t('fin.epsGrowth')} value={((latestEps - prevEps) / Math.abs(prevEps) * 100)}
                  fmt={(v) => pct(v)} color={v => v >= 0 ? 'success.main' : 'error.main'} />
              )}
              <FRow label={t('fin.latestRevenue')} value={latestRev} fmt={bigNum} />
              <FRow label={t('fin.profitMargin')} value={latestMargin} fmt={pct}
                color={v => v >= 10 ? 'success.main' : v >= 0 ? 'warning.main' : 'error.main'} />
              <FRow label={t('fin.freeCashFlow')} value={latestFcf} fmt={bigNum}
                color={v => v >= 0 ? 'success.main' : 'error.main'} />
              <FRow label={t('fin.currentRatio')} value={currentRatio} fmt={(v) => Number(v).toFixed(2)}
                color={v => v >= 1.5 ? 'success.main' : v >= 1.0 ? 'warning.main' : 'error.main'} />
              <FRow label={t('fin.debtAssets')} value={debtRatio} fmt={(v) => Number(v).toFixed(2)}
                color={v => v < 0.5 ? 'success.main' : v < 0.8 ? 'warning.main' : 'error.main'} />
            </TableBody></Table></TableContainer>
          </CardContent></Card>
        </Grid>

        {Object.keys(marketComp).length > 0 && (
          <Grid size={12}>
            <Card><CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('fin.perfVsMarket')}</Typography>
              <TableContainer><Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: 0, color: 'text.secondary', py: 0.6 }}></TableCell>
                    <TableCell align="right" sx={{ border: 0, py: 0.6, fontWeight: 600 }}>YTD</TableCell>
                    <TableCell align="right" sx={{ border: 0, py: 0.6, fontWeight: 600 }}>1-Year</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ border: 0, py: 0.6, fontWeight: 600 }}>{report?.ticker || 'Stock'}</TableCell>
                    <TableCell align="right" sx={{ border: 0, py: 0.6,
                      color: (pricePerf.ytd_return || 0) >= 0 ? 'success.main' : 'error.main' }}>
                      {pct(pricePerf.ytd_return || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ border: 0, py: 0.6,
                      color: (pricePerf.one_year_return || 0) >= 0 ? 'success.main' : 'error.main' }}>
                      {pct(pricePerf.one_year_return || 0)}
                    </TableCell>
                  </TableRow>
                  {Object.entries(marketComp).map(([name, d]) => (
                    <TableRow key={name}>
                      <TableCell sx={{ border: 0, py: 0.6, color: 'text.secondary' }}>{name}</TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.6,
                        color: (d.ytd_return || 0) >= 0 ? 'success.main' : 'error.main' }}>
                        {pct(d.ytd_return || 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.6,
                        color: (d.one_year_return || 0) >= 0 ? 'success.main' : 'error.main' }}>
                        {pct(d.one_year_return || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </CardContent></Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function FRow({ label, value, fmt, color }) {
  if (value == null || value === 'N/A' || value === 0) return null;
  const display = fmt ? fmt(value) : value;
  const c = color ? (typeof color === 'function' ? color(Number(value)) : color) : 'text.primary';
  return (
    <TableRow>
      <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.6 }}>{label}</TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.6, fontWeight: 600, color: c }}>{display}</TableCell>
    </TableRow>
  );
}
